import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, User, Lock, ArrowRight, ShieldCheck, AlertCircle, Plus, Search, Users, LogOut, Clock, Building, Phone, FileText, Car, CheckCircle2, ChevronRight, Menu, X, Filter, Download, MoreHorizontal, History, Settings, Bell, Mail, Camera, QrCode, Trash2, Trash, Eye, Pencil, XCircle } from 'lucide-react';
import { supabase } from './lib/supabase';
import { cn } from './lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Visitor, VisitorStatus, UserProfile } from './types';
enum OperationType { CREATE = 'create', UPDATE = 'update', DELETE = 'delete', LIST = 'list', GET = 'get', WRITE = 'write' }
function handleSupabaseError(error: any, operationType: OperationType, path: string | null) { console.error(error); throw error; }
const VAPID_PUBLIC_KEY = (import.meta as any).env.VITE_VAPID_PUBLIC_KEY || "BBUuxq0HlFvGb5zPz9_rIRmu_yLIkq64qAHawfaT7tDC3aXmPnyE6iRHd-ResKZig_S6o4fSCMCHUc4xPzRDVuU";

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function subscribeToPush(targetId: string, role: string) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn("Push API not supported");
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }

    const { error } = await supabase.from('push_subscriptions').upsert({
      target_id: targetId,
      role: role,
      subscription: JSON.parse(JSON.stringify(subscription))
    });

    if (error) {
      console.error('Failed to save push subscription:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error during push subscription:', err);
    return false;
  }
}

async function showLocalNotification(title: string, options?: NotificationOptions) {
  if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') return;
  
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      if (reg) {
        await reg.showNotification(title, options);
        return;
      }
    }
    new Notification(title, options);
  } catch (e) {
    console.error('Falha ao exibir notificação:', e);
    try {
      new Notification(title, options);
    } catch (fallbackErr) {
      console.error(fallbackErr);
    }
  }
}

// --- Components ---

const Button = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger', loading?: boolean, icon?: any }>(
  ({ className, variant = 'primary', loading, icon: Icon, children, ...props }, ref) => {
    const variants: any = {
      primary: 'bg-[#C81E1E] text-white hover:bg-[#A01818] shadow-xl shadow-red-900/20',
      secondary: 'bg-[#F2F4F6] text-[#2D2D2D] hover:bg-[#E6E8EA]',
      outline: 'border-2 border-[#C81E1E] text-[#C81E1E] hover:bg-[#C81E1E] hover:text-white',
      ghost: 'text-slate-500 hover:bg-slate-100',
      danger: 'bg-red-600 text-white hover:bg-red-700 shadow-lg',
    };
    return (
      <motion.button
        whileTap={{ scale: 0.98 }}
        ref={ref as any}
        disabled={loading}
        className={cn(
          'flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50 disabled:pointer-events-none',
          variants[variant],
          className
        )}
        {...props}
      >
        {loading ? (
          <div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
        ) : (
          <>
            {children}
            {Icon && <Icon className='w-4 h-4' />}
          </>
        )}
      </motion.button>
    );
  }
);

const Input = ({ label, icon: Icon, ...props }: any) => (
  <div className='space-y-1.5 w-full text-left'>
    {label && <label className='block text-[10px] font-bold uppercase text-slate-500 ml-1'>{label}</label>}
    <div className='relative'>
      {Icon && <Icon className='absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />}
      <input
        className={cn(
          'w-full bg-white border border-slate-200 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#C81E1E]/40 outline-none transition-all',
          Icon && 'pl-11'
        )}
        {...props}
      />
    </div>
  </div>
);

const formatPhone = (value: string) => {
  if (!value) return "";
  const phone = value.replace(/\D/g, "");
  return phone
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+?$/, "$1");
};

// --- Views ---

const VisitorRegistration = ({ onComplete }: { onComplete: (id: string) => void }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    entryTime: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    document: '',
    company: '',
    phone: '',
    sector: 'ADM',
    responsible: '',
    reason: '',
    vehicleType: 'A PÉ' as 'A PÉ' | 'VEÍCULO',
    vehicleModel: '',
    vehiclePlate: '',
  });
  const [loading, setLoading] = useState(false);
  const [agreed1, setAgreed1] = useState(false);
  const [agreed2, setAgreed2] = useState(false);
  const [agreed3, setAgreed3] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed1 || !agreed2 || !agreed3) return;
    setLoading(true);
    try {
      // Sanitization with DOMPurify
      const newId = crypto.randomUUID();
      const safeData = {
        id: newId,
        full_name: DOMPurify.sanitize(formData.fullName),
        entry_time: DOMPurify.sanitize(formData.entryTime),
        document: DOMPurify.sanitize(formData.document),
        company: DOMPurify.sanitize(formData.company),
        phone: DOMPurify.sanitize(formData.phone),
        sector: DOMPurify.sanitize(formData.sector),
        responsible: DOMPurify.sanitize(formData.responsible),
        reason: DOMPurify.sanitize(formData.reason),
        vehicle_type: formData.vehicleType,
        vehicle_model: DOMPurify.sanitize(formData.vehicleModel),
        vehicle_plate: DOMPurify.sanitize(formData.vehiclePlate),
        status: 'PENDING'
      };

      const { error } = await supabase
        .from('visitors')
        .insert([safeData]);

      if (error) throw error;

      onComplete(newId);
    } catch (error: any) {
      console.error("Error adding visitor:", error);
      alert("Erro ao enviar dados: " + (error.message || "Erro desconhecido") + "\n\nVerifique se você rodou os comandos SQL no painel do Supabase.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-lg mx-auto pb-24"
    >
      <header className="py-8">
        <h1 className="text-3xl font-extrabold text-[#C81E1E] tracking-tight">Registro de Visitante</h1>
        <p className="text-slate-500 text-sm mt-1">Insira as informAções para autorização de acesso.</p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-[#f2f4f6] p-5 rounded-2xl space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#C81E1E]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#C81E1E]">01. Identificação</h2>
          </div>
          <Input
            label="Nome Completo"
            placeholder="Digite seu nome"
            required
            value={formData.fullName}
            onChange={(e: any) => setFormData({ ...formData, fullName: e.target.value })}
          />
          <Input
            label="Horário de Entrada"
            placeholder="00:00"
            type="time"
            required
            value={formData.entryTime}
            onChange={(e: any) => setFormData({ ...formData, entryTime: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Documento (RG/CPF)"
              placeholder="Apenas números"
              required
              inputMode="numeric"
              type="tel"
              value={formData.document}
              onChange={(e: any) => setFormData({ ...formData, document: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="Telefone"
              placeholder="(00) 00000-0000"
              type="tel"
              value={formData.phone}
              onChange={(e: any) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
            />
          </div>
          <Input
            label="Empresa"
            placeholder="Nome da empresa"
            value={formData.company}
            onChange={(e: any) => setFormData({ ...formData, company: e.target.value })}
          />
        </section>

        <section className="bg-[#f2f4f6] p-5 rounded-2xl space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#C81E1E]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#C81E1E]">02. Dados da Visita</h2>
          </div>
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase text-slate-500 ml-1">Setor de Destino</label>
            <select
              className="w-full bg-white border border-slate-200 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#C81E1E]/40"
              value={formData.sector}
              onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
            >
              <option>Financeiro</option>
              <option>Produção Injeção</option>
              <option>Produção Resina</option>
              <option>Compras</option>
              <option>TI</option>
              <option>Diretoria</option>
              <option>Segurança do Trabalho</option>
              <option>RH</option>
              <option>Comercial</option>
              <option>SGI</option>
              <option>Vendas</option>
              <option>Qualidade</option>
              <option>Manutenção</option>
              <option>Logística</option>
              <option>Administrativo</option>
            </select>
          </div>
          <Input
            label="Responsável Interno"
            placeholder="Nome do colaborador"
            required
            value={formData.responsible}
            onChange={(e: any) => setFormData({ ...formData, responsible: e.target.value })}
          />
          <div className="space-y-1.5">
            <label className="block text-[10px] font-bold uppercase text-slate-500 ml-1">Motivo da Visita</label>
            <textarea
              className="w-full bg-white border border-slate-200 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#C81E1E]/40 min-h-[80px]"
              placeholder="Breve descrição do motivo"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            />
          </div>
        </section>

        <section className="bg-[#f2f4f6] p-5 rounded-2xl space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#C81E1E]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#C81E1E]">03. Veículo</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <motion.button
              type="button"
              onClick={() => setFormData({ ...formData, vehicleType: 'A PÉ' })}
              className={cn(
                "flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-bold text-xs",
                formData.vehicleType === 'A PÉ' ? "border-[#C81E1E] bg-white" : "border-transparent bg-white/50"
              )}
            >
              A PÉ
            </motion.button>
            <motion.button
              type="button"
              onClick={() => setFormData({ ...formData, vehicleType: 'VEÍCULO' })}
              className={cn(
                "flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-bold text-xs",
                formData.vehicleType === 'VEÍCULO' ? "border-[#C81E1E] bg-white" : "border-transparent bg-white/50"
              )}
            >
              VEÍCULO
            </motion.button>
          </div>
          {formData.vehicleType === 'VEÍCULO' && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="grid grid-cols-2 gap-3">
              <Input
                label="Modelo"
                placeholder="Ex: Corolla"
                value={formData.vehicleModel}
                onChange={(e: any) => setFormData({ ...formData, vehicleModel: e.target.value })}
              />
              <Input
                label="Placa"
                placeholder="ABC-1234"
                value={formData.vehiclePlate}
                onChange={(e: any) => setFormData({ ...formData, vehiclePlate: e.target.value })}
              />
            </motion.div>
          )}
        </section>

        <section className="bg-[#f2f4f6] p-5 rounded-2xl space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#C81E1E]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#C81E1E]">04. Termo de Ciência</h2>
          </div>
          <div className="bg-white/60 p-4 rounded-xl text-[11px] leading-relaxed text-slate-600 space-y-3 border border-slate-200">
            <p className="font-bold text-[#C81E1E]">Declaro estar ciente e de acordo com as regras abaixo durante minha permanência na empresa:</p>
            <ul className="list-decimal list-inside space-y-1.5">
              <li>Ao entrar na área produtiva utilizar a touca descartável, o protetor auricular, o protetor de barba e higienizar as mãos;</li>
              <li>Permanecer sempre acompanhado nas dependências da empresa;</li>
              <li>Utilizar os EPIs obrigatórios nas áreas produtivas;</li>
              <li>Não se alimentar ou consumir bebidas nas áreas produtivas;</li>
              <li>Não utilizar adornos nas áreas produtivas;</li>
              <li>Seguir estritamente as orientações de segurança e qualidade;</li>
              <li>Evitar contato direto com o produto;</li>
              <li>Comunicar qualquer situação de risco;</li>
            </ul>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-3 p-2 cursor-pointer group hover:bg-white/40 rounded-lg transition-colors">
              <input
                type="checkbox"
                className="w-5 h-5 shrink-0 mt-0.5 rounded border-slate-300 text-[#C81E1E] focus:ring-[#C81E1E] cursor-pointer"
                checked={agreed1}
                onChange={(e) => setAgreed1(e.target.checked)}
              />
              <span className="text-xs font-medium text-slate-600 text-left">
                Declaro estar ciente de todas as regras e me comprometo a segui-las integralmente durante minha permanência na empresa.
              </span>
            </label>

            <label className="flex items-start gap-3 p-2 cursor-pointer group hover:bg-white/40 rounded-lg transition-colors">
              <input
                type="checkbox"
                className="w-5 h-5 shrink-0 mt-0.5 rounded border-slate-300 text-[#C81E1E] focus:ring-[#C81E1E] cursor-pointer"
                checked={agreed2}
                onChange={(e) => setAgreed2(e.target.checked)}
              />
              <span className="text-xs font-medium text-slate-600 text-left">
                Declaro estar ciente de que o não cumprimento das normas internas poderá resultar na interrupção da visita.
              </span>
            </label>

            <label className="flex items-start gap-3 p-2 cursor-pointer group hover:bg-white/40 rounded-lg transition-colors">
              <input
                type="checkbox"
                className="w-5 h-5 shrink-0 mt-0.5 rounded border-slate-300 text-[#C81E1E] focus:ring-[#C81E1E] cursor-pointer"
                checked={agreed3}
                onChange={(e) => setAgreed3(e.target.checked)}
              />
              <span className="text-xs font-medium text-slate-600 text-left">
                Declaro que recebi meu kit de EPIs.
              </span>
            </label>
          </div>
        </section>

        <div className="pt-4">
          <motion.button
            type="submit"
            className={cn(
              "group relative w-full py-4 sm:py-5 rounded-2xl text-sm sm:text-base font-black tracking-widest uppercase transition-all duration-300 overflow-hidden",
              (!agreed1 || !agreed2 || !agreed3 || loading)
                ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                : "bg-gradient-to-r from-[#C81E1E] to-[#A01818] text-white shadow-[0_8px_30px_rgba(200,30,30,0.3)] hover:shadow-[0_8px_30px_rgba(200,30,30,0.5)] hover:-translate-y-1 active:translate-y-0 active:scale-[0.98]"
            )}
            disabled={!agreed1 || !agreed2 || !agreed3 || loading}
          >
            {/* Overlay effect for hover */}
            {(!agreed1 || !agreed2 || !agreed3 || loading) ? null : (
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            )}

            <div className="relative flex items-center justify-center z-10">
              {loading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                  <span>Processando...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <span>SOLICITAR ACESSO</span>
                  <LogOut className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              )}
            </div>
          </motion.button>
          {!agreed1 || !agreed2 || !agreed3 ? (
            <p className="text-[10px] text-center text-slate-400 mt-3 font-bold uppercase tracking-wider">Aceite os termos para habilitar o botão</p>
          ) : null}
        </div>
      </form>
    </motion.div>
  );
};

const SuccessScreen = ({ visitorId, onReset }: { visitorId: string, onReset: () => void }) => {
  const [visitor, setVisitor] = useState<Visitor | null>(null);
  const [visitorStatus, setVisitorStatus] = useState<VisitorStatus>('PENDING');
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    if (!visitorId) return;

    // Tenta puxar dados, porem pode falhar por causa das regras de RLS (anônimos não podem ler)
    const fetchVisitor = async () => {
      const { data } = await supabase
        .from('visitors')
        .select('*')
        .eq('id', visitorId)
        .single();

      if (data) {
        setVisitor({
          ...data,
          fullName: data.full_name,
          responsibleEmail: data.responsible_email
        } as any);
        if (data.status) setVisitorStatus(data.status);
      }
    };
    fetchVisitor();

    // Utiliza Broadcast em vez de postgres_changes para driblar o bloqueio de RLS no frontend
    const channel = supabase
      .channel(`visitor-${visitorId}`)
      .on('broadcast', { event: 'status-update' }, (payload) => {
        const newStatus = payload.payload?.status;
        if (newStatus) {
          setVisitorStatus(newStatus);
          
          if (newStatus === 'APPROVED' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            showLocalNotification('Acesso Liberado! ✅', { body: 'Seu responsável aprovou a sua entrada. Dirija-se à catraca.' });
          }
          if (newStatus === 'REJECTED' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            showLocalNotification('Acesso Negado ❌', { body: 'Seu responsável recusou a sua entrada. Por favor, fale com a recepção.' });
          }
        }
      })
      .subscribe();

    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      subscribeToPush(visitorId, 'VISITOR');
      setPushEnabled(true);
    }

    return () => {
      // supabase.removeChannel(channel);
    };
  }, [visitorId]);

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-md mx-auto pt-12 pb-24 text-center"
    >
      <div className="relative w-32 h-32 bg-[#C81E1E] rounded-2xl mx-auto flex items-center justify-center shadow-2xl mb-8">
        <CheckCircle2 className="w-16 h-16 text-[#a3f69c]" />
        <div
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 bg-[#C81E1E] rounded-2xl -z-10"
        />
      </div>

      <h1 className="text-3xl font-extrabold text-[#C81E1E] mb-4">Dados enviados com sucesso!</h1>
      <p className="text-slate-500 mb-8">Aguarde a liberação do seu responsável na recepção.</p>

      {!pushEnabled && (typeof Notification !== 'undefined' && Notification.permission !== 'denied') && (
        <div className="mb-8 p-4 bg-[#C81E1E]/5 rounded-2xl border border-[#C81E1E]/10 flex flex-col items-center gap-3">
          <Bell className="w-6 h-6 text-[#C81E1E]" />
          <p className="text-xs font-bold text-[#C81E1E]">Deseja receber notificações sobre seu status?</p>
          <Button
            variant="secondary"
            className="text-[10px] py-2 h-auto"
            onClick={async () => {
              if (typeof Notification !== 'undefined') {
                const perm = await Notification.requestPermission();
                if (perm === 'granted') {
                  const success = await subscribeToPush(visitorId, 'VISITOR');
                  if (success) setPushEnabled(true);
                  showLocalNotification('Notificações ativadas', { body: 'Você será avisado quando for liberado!' });
                }
              }
            }}
          >
            ATIVAR NOTIFICAÇÕES
          </Button>
        </div>
      )}

      <div className="space-y-4 text-left">
        <div className="bg-white p-5 rounded-2xl shadow-sm flex items-center gap-4 border-l-4 border-[#a3f69c]">
          <div className="w-10 h-10 bg-[#a3f69c]/20 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#005312]" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400">Etapa 1</p>
            <h3 className="font-bold text-[#C81E1E]">Formulário Recebido</h3>
          </div>
          <CheckCircle2 className="ml-auto w-5 h-5 text-[#a3f69c]" />
        </div>

        <div className={cn(
          "bg-white p-5 rounded-2xl shadow-sm flex items-center gap-4 border-l-4 transition-all",
          visitorStatus === 'PENDING' ? "border-[#C81E1E]" : "border-[#a3f69c]"
        )}>
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-[#C81E1E]" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400">Etapa 2</p>
            <h3 className="font-bold text-[#C81E1E]">
              {visitorStatus === 'PENDING' ? 'Notificando responsável' : 'Responsável Notificado'}
            </h3>
          </div>
          {visitorStatus === 'PENDING' ? (
            <div className="ml-auto w-2 h-2 bg-[#a3f69c] rounded-full animate-pulse" />
          ) : (
            <CheckCircle2 className="ml-auto w-5 h-5 text-[#a3f69c]" />
          )}
        </div>

        <div className={cn(
          "bg-white p-5 rounded-2xl shadow-sm flex items-center gap-4 border-l-4 transition-all",
          visitorStatus === 'APPROVED' ? "border-[#a3f69c]" : "opacity-50"
        )}>
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-[#C81E1E]" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400">Etapa 3</p>
            <h3 className="font-bold text-[#C81E1E]">Acesso Liberado</h3>
          </div>
          {visitorStatus === 'APPROVED' && <CheckCircle2 className="ml-auto w-5 h-5 text-[#a3f69c]" />}
          {visitorStatus === 'REJECTED' && <Search className="w-4 h-4" />}
        </div>
      </div>

      {visitorStatus === 'REJECTED' && (
        <div className="mt-8 p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-3">
          <LogOut className="w-5 h-5" />
          <p className="text-xs font-bold">Acesso negado pelo responsável. Dirija-se à recepção.</p>
        </div>
      )}

      <Button variant="outline" className="mt-12 w-full" onClick={onReset}>
        NOVO REGISTRO
      </Button>
    </motion.div>
  );
};

const VisitorDetailsModal = ({ visitor, onClose }: { visitor: Visitor | null, onClose: () => void }) => {
  if (!visitor) return null;

  const formatDate = (dateString: any) => {
    if (!dateString) return '---';
    if (typeof dateString === 'string' && /^\d{2}:\d{2}$/.test(dateString)) return dateString;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pendente';
      case 'APPROVED': return 'No Prédio';
      case 'REJECTED': return 'Recusado';
      case 'CHECKED_OUT': return 'Saída';
      default: return status;
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
        <motion.div initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-[#C81E1E]/60 backdrop-blur-sm"
        />
        <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#C81E1E] flex items-center justify-center text-white font-black text-xl">
                {(visitor.fullName || '?').charAt(0)}
              </div>
              <div>
                <h3 className="text-xl font-black text-[#C81E1E]">{visitor.fullName}</h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{visitor.company || 'Visitante Particular'}</p>
              </div>
            </div>
            <motion.button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
              <X className="w-6 h-6 text-slate-400" />
            </motion.button>
          </div>

          <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
              <div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Informações de Contato</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-[#C81E1E]">
                    <ShieldCheck className="w-4 h-4 opacity-40" />
                    <span className="font-bold text-[10px] uppercase text-slate-400 w-20">Documento:</span>
                    <span className="font-medium">{visitor.document}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#C81E1E]">
                    <ShieldCheck className="w-4 h-4 opacity-40" />
                    <span className="font-bold text-[10px] uppercase text-slate-400 w-20">Telefone:</span>
                    <span className="font-medium">{visitor.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#C81E1E]">
                    <ShieldCheck className="w-4 h-4 opacity-40" />
                    <span className="font-bold text-[10px] uppercase text-slate-400 w-20">Empresa:</span>
                    <span className="font-medium">{visitor.company || 'Particular'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Detalhes da Visita</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-[#C81E1E]">
                    <ShieldCheck className="w-4 h-4 opacity-40" />
                    <span className="font-bold text-[10px] uppercase text-slate-400 w-20">Setor:</span>
                    <span className="font-medium">{visitor.sector}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#C81E1E]">
                    <ShieldCheck className="w-4 h-4 opacity-40" />
                    <span className="font-bold text-[10px] uppercase text-slate-400 w-20">Responsável:</span>
                    <span className="font-medium">{visitor.responsible}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#C81E1E]">
                    <ShieldCheck className="w-4 h-4 opacity-40" />
                    <span className="font-bold text-[10px] uppercase text-slate-400 w-20">Previsto:</span>
                    <span className="font-medium text-blue-600 font-bold">{visitor.entryTime}</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-[#C81E1E] bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <FileText className="w-4 h-4 opacity-40 mt-0.5" />
                    <div>
                      <p className="text-[8px] font-black uppercase text-slate-400 mb-1">Motivo da Visita</p>
                      <span className="font-medium leading-relaxed">{visitor.reason}</span>
                    </div>
                  </div>
                </div>
              </div>

              {visitor.vehicleType === 'VEÍCULO' && (
                <div className="bg-[#C81E1E]/5 p-4 rounded-2xl border border-[#C81E1E]/10">
                  <h4 className="text-[10px] font-black text-[#C81E1E] uppercase tracking-[0.2em] mb-3">Transporte: VEÍCULO</h4>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 text-sm text-[#C81E1E]">
                      <ShieldCheck className="w-4 h-4 opacity-40" />
                      <div>
                        <p className="text-[8px] font-black uppercase text-slate-400">Modelo</p>
                        <span className="font-bold uppercase">{visitor.vehicleModel}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#C81E1E]">
                      <div className="w-2 h-2 rounded-full bg-[#C81E1E] opacity-20" />
                      <div>
                        <p className="text-[8px] font-black uppercase text-slate-400">Placa</p>
                        <span className="font-bold uppercase tracking-wider">{visitor.vehiclePlate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
              <h4 className="text-[10px] font-black text-[#C81E1E] uppercase tracking-[0.2em] mb-6">Linha do Tempo</h4>
              <div className="space-y-8 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                <div className="relative pl-8">
                  <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-white border-4 border-[#C81E1E] z-10" />
                  <p className="text-[10px] font-black text-slate-400 uppercase">Solicitação</p>
                  <p className="text-sm font-bold text-[#C81E1E]">{formatDate(visitor.createdAt)}</p>
                </div>
                <div className="relative pl-8">
                  <div className={cn("absolute left-0 top-1 w-6 h-6 rounded-full bg-white border-4 z-10", visitor.entryTime ? "border-green-500" : "border-slate-200")} />
                  <p className="text-[10px] font-black text-slate-400 uppercase">Entrada</p>
                  <p className="text-sm font-bold text-[#C81E1E]">{formatDate(visitor.entryTime)}</p>
                </div>
                <div className="relative pl-8">
                  <div className={cn("absolute left-0 top-1 w-6 h-6 rounded-full bg-white border-4 z-10", visitor.exitTime ? "border-red-500" : "border-slate-200")} />
                  <p className="text-[10px] font-black text-slate-400 uppercase">Saída</p>
                  <p className="text-sm font-bold text-[#C81E1E]">{formatDate(visitor.exitTime)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
            <Button variant="secondary" onClick={onClose}>Fechar</Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const EditVisitorModal = ({
  visitor,
  onClose,
  onSave
}: {
  visitor: Visitor | null,
  onClose: () => void,
  onSave: (v: Visitor) => Promise<void>
}) => {
  const [editingData, setEditingData] = useState<Visitor | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditingData(visitor ? { ...visitor } : null);
  }, [visitor]);

  if (!visitor || !editingData) return null;

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(editingData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden z-10"
        >
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Pencil className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Editar Visitante</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Atualizar informações</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white rounded-xl transition-colors shadow-sm text-slate-400"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
            <Input
              label="Nome Completo"
              value={editingData.fullName}
              onChange={(e: any) => setEditingData({ ...editingData, fullName: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Documento"
                value={editingData.document}
                onChange={(e: any) => setEditingData({ ...editingData, document: e.target.value.replace(/\D/g, '') })}
              />
              <Input
                label="Telefone"
                value={editingData.phone}
                onChange={(e: any) => setEditingData({ ...editingData, phone: e.target.value })}
              />
            </div>
            <Input
              label="Empresa"
              value={editingData.company}
              onChange={(e: any) => setEditingData({ ...editingData, company: e.target.value })}
            />
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase text-slate-500 ml-1">Setor de Destino</label>
              <select
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#C81E1E]/40"
                value={editingData.sector}
                onChange={(e) => setEditingData({ ...editingData, sector: e.target.value })}
              >
                <option>Financeiro</option>
                <option>Produção Injeção</option>
                <option>Produção Resina</option>
                <option>Compras</option>
                <option>TI</option>
                <option>Diretoria</option>
                <option>Segurança do Trabalho</option>
                <option>RH</option>
                <option>Comercial</option>
                <option>SGI</option>
                <option>Vendas</option>
                <option>Qualidade</option>
                <option>Manutenção</option>
                <option>Logística</option>
                <option>Administrativo</option>
              </select>
            </div>
            <Input
              label="Responsável Interno"
              value={editingData.responsible}
              onChange={(e: any) => setEditingData({ ...editingData, responsible: e.target.value })}
            />
          </div>

          <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleSave}
              loading={isSaving}
            >
              Salvar Alterações
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }: { isOpen: boolean, onClose: () => void, onConfirm: () => void }) => {
  if (!isOpen) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
        <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center" >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Trash2 className="w-10 h-10 text-[#C81E1E]" />
          </div>
          <h3 className="text-xl font-black text-slate-900 mb-2">Excluir Registro?</h3>
          <p className="text-sm text-slate-500 mb-8">Esta ação não pode ser desfeita. O registro será removido permanentemente.</p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={() => { onConfirm(); onClose(); }}>Excluir</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const AdminDashboard = ({ user, setView }: { user: any, setView: (v: string) => void }) => {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [filter, setFilter] = useState<VisitorStatus | 'ALL'>('ALL');
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState<Visitor | null>(null);
  const [visitorToDelete, setVisitorToDelete] = useState<string | null>(null);

  useEffect(() => {
    // Pedir permissão ao admin para notificações
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [user]);

  useEffect(() => {
    const fetchVisitors = async () => {
      const { data, error } = await supabase
        .from('visitors')
        .select('*')
        .order('created_at', { ascending: false });

      if (data) {
        setVisitors(data.map(v => ({
          ...v,
          fullName: v.full_name || 'Visitante sem nome',
          responsibleEmail: v.responsible_email || '',
          vehicleType: v.vehicle_type,
          vehicleModel: v.vehicle_model,
          vehiclePlate: v.vehicle_plate,
          createdAt: v.created_at,
          entryTime: v.entry_time,
          exitTime: v.exit_time
        } as any)));
      }
    };

    fetchVisitors();

    const channel = supabase
      .channel('admin-visitors')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'visitors' }, (payload) => {
        fetchVisitors(); // Refresh list on any change

        if (payload.eventType === 'INSERT' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          showLocalNotification('Novo Visitante!', {
            body: `${payload.new.full_name || 'Alguém'} acabou de registrar a chegada no totem.`
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateStatus = async (id: string, status: VisitorStatus) => {
    try {
      const now = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      const { error } = await supabase
        .from('visitors')
        .update({
          status,
          updated_at: new Date().toISOString(),
          ...(status === 'APPROVED' ? { entry_time: now } : {}),
          ...(status === 'CHECKED_OUT' ? { exit_time: now } : {}),
        })
        .eq('id', id);

      if (error) throw error;

      // Dispara um broadcast para bypassar a falta de permissão de leitura do front-end anônimo
      const channel = supabase.channel(`visitor-${id}`);
      await channel.send({
        type: 'broadcast',
        event: 'status-update',
        payload: { status }
      });
    } catch (error: any) {
      console.error("Error updating status:", error);
      alert("Erro ao atualizar status: " + (error.message || "Erro desconhecido") + "\n\nVerifique se rodou o comando SQL para adicionar a coluna exit_time.");
    }
  };

  const handleDelete = async (id: string) => {

    try {
      const { error } = await supabase
        .from('visitors')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setVisitors(prev => prev.filter(v => v.id !== id));
    } catch (error: any) {
      console.error("Error deleting visitor:", error);
      alert("Erro ao excluir: " + error.message);
    }
  };

  const filteredVisitors = visitors.filter(v => {
    // 1. Filter by Role/Sector
    if (user.role === 'GESTOR') {
      if (v.sector !== user.sector) return false;
    }

    // 2. Filter by Status/Search
    const matchesFilter = filter === 'ALL' || v.status === filter;
    const name = v.fullName || '';
    const doc = v.document || '';
    const resp = v.responsible || '';
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.includes(searchTerm) ||
      resp.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString: any) => {
    if (!dateString) return '---';
    // Se jÃƒÂ¡ estiver no formato hh:mm, retorna como estÃƒÂ¡
    if (typeof dateString === 'string' && /^\d{2}:\d{2}$/.test(dateString)) {
      return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const calculateAverageTime = () => {
    const completedVisits = visitors.filter(v => (v.status === 'APPROVED' || v.status === 'CHECKED_OUT') && v.entryTime && v.createdAt);
    if (completedVisits.length === 0) return '---';

    let totalMinutes = 0;
    completedVisits.forEach(v => {
      try {
        const start = new Date(v.createdAt);
        let end: Date;

        if (typeof v.entryTime === 'string' && v.entryTime.includes(':') && v.entryTime.length === 5) {
          const [hours, minutes] = v.entryTime.split(':').map(Number);
          end = new Date(start);
          end.setHours(hours, minutes, 0, 0);
        } else {
          end = new Date(v.entryTime);
        }

        const diffMs = end.getTime() - start.getTime();
        const diffMins = Math.max(0, Math.floor(diffMs / 60000));
        totalMinutes += diffMins;
      } catch (e) {
        // Skip invalid records
      }
    });

    const avg = Math.round(totalMinutes / completedVisits.length);
    if (avg === 0) return '< 1 min';
    return avg < 60 ? `${avg} min` : `${Math.floor(avg / 60)}h ${avg % 60}m`;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pendente';
      case 'APPROVED': return 'No Prédio';
      case 'REJECTED': return 'Recusado';
      case 'CHECKED_OUT': return 'Saída';
      default: return status;
    }
  };

  const exportToCSV = () => {
    const headers = ["Data", "Visitante", "Documento", "Empresa", "Telefone", "Setor", "Responsável", "Motivo", "Status", "Entrada", "Saída"];
    const rows = filteredVisitors.map(v => [
      formatDate(v.createdAt),
      v.fullName,
      v.document,
      v.company,
      v.phone,
      v.sector,
      v.responsible,
      v.reason,
      getStatusLabel(v.status),
      v.entryTime || '---',
      v.exitTime || '---'
    ]);

    const csvContent = "\uFEFF" + [headers, ...rows].map(e => e.join(";")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `relatorio_visitantes_${new Date().toLocaleDateString('pt-BR')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  const exportToPDF = () => {
    alert("ExportaÃƒÂ§ÃƒÂ£o para PDF desativada temporariamente para diagnÃƒÂ³stico.");
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="text-3xl font-extrabold text-[#2D2D2D]">Painel Administrativo</h1>
          <div className="flex items-center gap-4 mt-1">
            <p className="text-slate-500 font-medium">Monitoring status: <span className="text-green-600 font-bold uppercase tracking-widest text-[10px] ml-1 bg-green-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>Active</span></p>
            {!pushEnabled && (typeof Notification !== 'undefined' && Notification.permission !== 'denied') && (
              <Button
                variant="ghost"
                onClick={async () => {
                  const success = await subscribeToPush(user.id, 'ADMIN');
                  if (success) setPushEnabled(true);
                }}
                className="text-[10px] font-bold text-[#C81E1E] underline flex items-center gap-1 p-0 h-auto hover:bg-transparent"
              >
                <Bell className="w-4 h-4" /> ATIVAR NOTIFICAÇÕES
              </Button>
            )}
          </div>
        </div>
        <div className="flex gap-3 relative items-center">
          <div className="relative">
            <Button
              variant="secondary"
              className="text-sm px-4 h-[42px] gap-2 font-bold"
              onClick={() => setShowExportOptions(!showExportOptions)}
            >
              <Download className="w-4 h-4" /> Exportar Relatório
            </Button>
            <div>
              {showExportOptions && (
                <motion.div initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-2xl border border-slate-100 z-[60] overflow-hidden"
                >
                  {/* <motion.button onClick={exportToPDF} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-slate-50 transition-colors flex items-center gap-2 text-[#C81E1E]">
                    <div className="w-2 h-2 rounded-full bg-red-500" /> PDF Documento
                  </motion.button> */}
                  <motion.button onClick={exportToCSV} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-slate-50 transition-colors flex items-center gap-2 text-[#C81E1E]">
                    <div className="w-2 h-2 rounded-full bg-green-500" /> CSV Planilha
                  </motion.button>
                </motion.div>
              )}
            </div>
          </div>
          <Button
            variant="primary"
            className="text-sm px-4 h-[42px] gap-2 font-bold shadow-none"
            onClick={() => setView('REGISTRATION')}
          >
            <Plus className="w-4 h-4" /> Novo Visitante
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <div className="bg-white p-6 rounded-2xl shadow-sm border-l-4 border-[#C81E1E]">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Total Hoje</p>
          <h3 className="text-4xl font-black text-[#C81E1E] mt-1">{visitors.length}</h3>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border-l-4 border-[#a3f69c]">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">No Prédio</p>
          <h3 className="text-4xl font-black text-[#C81E1E] mt-1">
            {visitors.filter(v => v.status === 'APPROVED').length}
          </h3>
        </div>
        <div className="md:col-span-2 bg-[#2D2D2D] p-6 rounded-2xl shadow-xl shadow-slate-200 text-white flex items-center justify-between overflow-hidden relative">
          <div className="relative z-10">
            <p className="text-[10px] font-black opacity-60 uppercase tracking-[0.2em]">Tempo MÉDIO de LIBERAÇÃO</p>
            <div className="flex items-baseline gap-2 mt-1">
              <h3 className="text-4xl font-black">{calculateAverageTime()}</h3>
              <span className="text-[10px] font-bold opacity-40 uppercase">por visita</span>
            </div>
          </div>
          <div className="relative z-10 text-right">
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full backdrop-blur-md">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              <p className="text-[8px] font-black uppercase tracking-widest">Tempo Real</p>
            </div>
          </div>
          {/* Decorative Background Icon */}
          <Search className="w-4 h-4" />
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h4 className="font-bold text-lg text-[#C81E1E]">Histórico de Acessos</h4>
          <div className="flex items-center gap-3">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
              <Input
                className="bg-slate-100 border-none rounded-xl pl-10 pr-4 py-2 text-sm w-full"
                placeholder="Buscar por nome, doc ou Responsável..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="bg-slate-100 border-none rounded-xl px-4 py-2 text-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
            >
              <option value="ALL">Todos</option>
              <option value="PENDING">Pendentes</option>
              <option value="APPROVED">No Prédio</option>
              <option value="CHECKED_OUT">Saída</option>
              <option value="REJECTED">Recusados</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-400 font-bold border-b border-slate-50">
                <th className="p-6 text-left">Visitante</th>
                <th className="p-6 text-left">Solicitação</th>
                <th className="p-6">Setor / Responsável</th>
                <th className="p-6">Status</th>
                <th className="p-6">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredVisitors.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-[#C81E1E]">
                        {(v.fullName || '?').charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-[#C81E1E]">{v.fullName || 'Visitante sem nome'}</p>
                        <p className="text-xs text-slate-500">{v.document || '---'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-6">
                    <p className="font-bold text-xs text-slate-500 whitespace-nowrap">{formatDate(v.createdAt)}</p>
                  </td>
                  <td className="p-6">
                    <p className="font-bold text-xs text-[#C81E1E]">{v.sector}</p>
                    <p className="text-xs text-slate-500">{v.responsible}</p>
                  </td>
                  <td className="p-6">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter inline-flex items-center gap-1",
                      v.status === 'PENDING' && "bg-yellow-100 text-yellow-700",
                      v.status === 'APPROVED' && "bg-green-100 text-green-700",
                      v.status === 'REJECTED' && "bg-red-100 text-red-700",
                      v.status === 'CHECKED_OUT' && "bg-slate-100 text-slate-700",
                    )}>
                      {getStatusLabel(v.status)}
                    </span>
                  </td>
                  <td className="p-6">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => setSelectedVisitor(v)}
                        className="p-2 text-slate-400 hover:text-[#C81E1E] hover:bg-slate-100 rounded-lg transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>

                      {user.role !== 'PORTEIRO' && (
                        <>
                          <Button
                            variant="ghost"
                            onClick={() => setEditingVisitor(v)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar registro"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            onClick={() => setVisitorToDelete(v.id!)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir registro"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}

                      {(user.role === 'ADM' || user.role === 'GESTOR' || user.role === 'ADMINISTRADOR') && v.status === 'PENDING' && (
                        <>
                          <Button
                            variant="ghost"
                            onClick={() => updateStatus(v.id!, 'APPROVED')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Aprovar"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => updateStatus(v.id!, 'REJECTED')}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Recusar"
                          >
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </>
                      )}
                      {v.status === 'APPROVED' && user.role !== 'PORTEIRO' && (
                        <Button
                          variant="ghost"
                          onClick={() => updateStatus(v.id!, 'CHECKED_OUT')}
                          className="text-xs font-bold text-[#C81E1E] hover:underline"
                        >
                          Registrar Saída
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <VisitorDetailsModal
        visitor={selectedVisitor}
        onClose={() => setSelectedVisitor(null)}
      />

      <DeleteConfirmationModal isOpen={!!visitorToDelete} onClose={() => setVisitorToDelete(null)} onConfirm={() => handleDelete(visitorToDelete!)} />
      <EditVisitorModal
        visitor={editingVisitor}
        onClose={() => setEditingVisitor(null)}
        onSave={async (updatedData) => {
          try {
            const { error } = await supabase
              .from('visitors')
              .update({
                full_name: updatedData.fullName,
                document: updatedData.document,
                phone: updatedData.phone,
                company: updatedData.company,
                sector: updatedData.sector,
                responsible: updatedData.responsible,
                updated_at: new Date().toISOString()
              })
              .eq('id', updatedData.id);

            if (error) throw error;

            setVisitors(prev => prev.map(v => v.id === updatedData.id ? updatedData : v));
            setEditingVisitor(null);
            alert("Registro atualizado com sucesso!");
          } catch (error: any) {
            alert("Erro ao atualizar: " + error.message);
          }
        }}
      />

    </div>
  );
};

const UserSettings = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: '', role: 'PORTEIRO', sector: 'ADM' });
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const fetchUsers = async () => {
    const { data } = await supabase.from('authorized_users').select('*');
    if (data) setUsers(data);
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.from('authorized_users').insert([newUser]);
    if (!error) {
      setNewUser({ email: '', role: 'PORTEIRO', sector: 'ADM' });
      fetchUsers();
    } else {
      alert("Erro ao adicionar usuário: " + error.message);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('authorized_users').delete().eq('id', id);
    if (error) {
      alert("Erro ao excluir usuário: " + error.message);
    } else {
      fetchUsers();
    }
    setUserToDelete(null);
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-[#C81E1E]">Configurações de Usuário</h1>
        <p className="text-slate-500">Gerencie quem pode acessar o painel e seus níveis de permissão.</p>
      </header>

      <form onSubmit={handleAddUser} className="bg-white p-6 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col md:flex-row gap-6 items-end mb-10">
        <div className="flex-1 w-full">
          <Input
            label="E-mail do Usuário"
            placeholder="usuario@empresa.com"
            icon={Mail}
            required
            value={newUser.email}
            onChange={(e: any) => setNewUser({ ...newUser, email: e.target.value })}
          />
        </div>
        <div className="w-full md:w-40 space-y-1.5 text-left">
          <label className="block text-[10px] font-bold uppercase text-slate-500 ml-1">Nível de Acesso</label>
          <div className="relative">
            <select
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#C81E1E]/40 outline-none appearance-none transition-all"
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="ADM">ADM</option>
              <option value="PORTEIRO">Porteiro</option>
              <option value="GESTOR">Gestor</option>
            </select>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
          </div>
        </div>
        <div className="w-full md:w-48 space-y-1.5 text-left">
          <label className="block text-[10px] font-bold uppercase text-slate-500 ml-1">Setor</label>
          <div className="relative">
            <select
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#C81E1E]/40 outline-none appearance-none transition-all"
              value={newUser.sector}
              onChange={(e) => setNewUser({ ...newUser, sector: e.target.value })}
            >
              <option>Financeiro</option>
              <option>Produção Injeção</option>
              <option>Produção Resina</option>
              <option>Compras</option>
              <option>TI</option>
              <option>Diretoria</option>
              <option>Segurança do Trabalho</option>
              <option>RH</option>
              <option>Comercial</option>
              <option>SGI</option>
              <option>Vendas</option>
              <option>Qualidade</option>
              <option>Manutenção</option>
              <option>Logística</option>
              <option>Administrativo</option>
            </select>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
          </div>
        </div>
        <Button type="submit" className="px-8 w-full md:w-auto h-[46px]" icon={Plus}>
          ADICIONAR
        </Button>
      </form>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-100 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-400">
            <tr>
              <th className="p-4">E-mail</th>
              <th className="p-4">Nível</th>
              <th className="p-4">Setor</th>
              <th className="p-4 text-right">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map(u => (
              <tr key={u.id}>
                <td className="p-4 text-sm font-medium text-[#C81E1E]">{u.email}</td>
                <td className="p-4">
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-bold",
                    u.role === 'ADM' ? "bg-purple-100 text-purple-700" :
                      u.role === 'PORTEIRO' ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"
                  )}>
                    {u.role}
                  </span>
                </td>
                <td className="p-4 text-xs text-slate-500">{u.role === 'GESTOR' ? u.sector : '---'}</td>
                <td className="p-4 text-right">
                  <Button variant="ghost" onClick={() => setUserToDelete(u.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteConfirmationModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={() => handleDelete(userToDelete!)}
      />
    </div>
  );
};

// --- Main App ---

export default function App() {
  const [view, setView] = useState<'REGISTRATION' | 'SUCCESS' | 'ADMIN' | 'SETTINGS'>('REGISTRATION');
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    const checkUser = async (sessionUser: any, event?: string) => {
      if (!sessionUser) {
        setUser(null);
        setView('REGISTRATION');
        return;
      }

      // Check if user is in authorized_users table
      const { data: allUsers, error: listError } = await supabase
        .from('authorized_users')
        .select('email, role, sector');

      const data = allUsers?.find(u => u.email.toLowerCase().trim() === sessionUser.email.toLowerCase().trim());

      if (data) {
        setUser({ ...sessionUser, role: data.role, sector: data.sector });
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') setView('ADMIN');
      } else {
        // Logged in but not authorized to see admin panel
        setUser({ ...sessionUser, role: 'UNAUTHORIZED' });
        setView('REGISTRATION');
        if (sessionUser && event && event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN') {
          alert("Acesso Negado: Seu e-mail (" + sessionUser.email + ") não está cadastrado como administrador.");
        }
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      checkUser(session?.user ?? null, 'INITIAL_SESSION').finally(() => setLoading(false));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      checkUser(session?.user ?? null, event);
    });

    return () => subscription.unsubscribe();
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
    } catch (error: any) {
      console.error("Login error:", error);
      alert("Erro ao entrar com Google. Verifique se o provedor estÃƒÂ¡ ativo no Supabase.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      // View will be updated by onAuthStateChange
    } catch (error: any) {
      console.error("Login error:", error);
      alert("Erro ao entrar: " + (error.message || "Verifique suas credenciais."));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setView('REGISTRATION');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
      <div className="w-12 h-12 border-4 border-[#C81E1E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-[#f8f9fb] text-[#191c1e] font-['Inter']">
        {/* Top Bar */}
        <header className="fixed top-0 w-full z-50 bg-[#f8f9fb]/80 backdrop-blur-md border-b border-slate-200/50 px-6 h-16 flex justify-between items-center">
          <motion.button
            onClick={() => setView('REGISTRATION')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Bell className="w-6 h-6 text-[#C81E1E]" />
            <span className="text-xl font-black text-[#2D2D2D] uppercase tracking-widest font-['Manrope']">RPX - CONTROLE DE VISITANTES </span>
          </motion.button>

          <div className="flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-6">
                <nav className="hidden md:flex items-center gap-4 border-r border-slate-200 pr-6 mr-2">
                  <motion.button
                    onClick={() => setView('REGISTRATION')}
                    className={cn("text-[10px] font-bold uppercase tracking-widest transition-colors", view === 'REGISTRATION' ? "text-[#C81E1E]" : "text-slate-400 hover:text-[#C81E1E]")}
                  >
                    Registro
                  </motion.button>
                  <motion.button
                    onClick={() => setView('ADMIN')}
                    className={cn("text-[10px] font-bold uppercase tracking-widest transition-colors", view === 'ADMIN' ? "text-[#C81E1E]" : "text-slate-400 hover:text-[#C81E1E]")}
                  >
                    Painel
                  </motion.button>
                  {user.role === 'ADM' && (
                    <motion.button
                      onClick={() => setView('SETTINGS')}
                      className={cn("text-[10px] font-bold uppercase tracking-widest transition-colors", view === 'SETTINGS' ? "text-[#C81E1E]" : "text-slate-400 hover:text-[#C81E1E]")}
                    >
                      Ajustes
                    </motion.button>
                  )}
                </nav>

                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs font-bold text-[#C81E1E]">{user.user_metadata?.full_name || user.email}</p>
                    <p className="text-[10px] text-slate-500 uppercase font-bold">{user.role === 'ADM' ? 'adm' : user.role === 'GESTOR' ? `Gestor (${user.sector})` : 'Porteiro'}</p>
                  </div>
                  <motion.button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-500 transition-colors" title="Sair">
                    <LogOut className="w-5 h-5" />
                  </motion.button>
                  <img
                    src={user.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${user.email}&background=C81E1E&color=fff`}
                    className="w-8 h-8 rounded-full border border-slate-200 cursor-pointer"
                    alt="User"
                    onClick={() => setView('ADMIN')}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {showEmailLogin ? (
                  <form onSubmit={handleEmailLogin} className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4 bg-white p-2 rounded-xl shadow-lg border border-slate-100 absolute right-6 top-16 md:relative md:top-0 md:right-0 md:shadow-none md:border-none">
                    <Input
                      type="email"
                      placeholder="E-mail"
                      className="text-[10px] p-2 border rounded-lg border-slate-200 focus:ring-1 ring-[#C81E1E]/20 outline-none w-32"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <Input
                      type="password"
                      placeholder="Senha"
                      className="text-[10px] p-2 border rounded-lg border-slate-200 focus:ring-1 ring-[#C81E1E]/20 outline-none w-24"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <motion.button
                      type="submit"
                      disabled={loginLoading}
                      className="text-[10px] font-bold bg-[#C81E1E] text-white px-4 py-2 rounded-lg uppercase hover:bg-opacity-90 transition-all"
                    >
                      {loginLoading ? '...' : 'Entrar'}
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => setShowEmailLogin(false)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </form>
                ) : (
                  <motion.button
                    onClick={() => setShowEmailLogin(true)}
                    className="p-2 w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-[#C81E1E] transition-all"
                    title="Acesso Administrativo"
                  >
                    <LogOut className="w-5 h-5" />
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </header>

        <main className="pt-20 px-6">
          <AnimatePresence mode="wait">
            {view === 'REGISTRATION' && (
              <div
                key="reg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <VisitorRegistration
                  onComplete={(id) => {
                    setVisitorId(id);
                    setView('SUCCESS');
                  }}
                />
              </div>
            )}
            {view === 'SUCCESS' && (
              <div
                key="success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <SuccessScreen
                  visitorId={visitorId!}
                  onReset={() => setView('REGISTRATION')}
                />
              </div>
            )}
            {view === 'ADMIN' && user && (
              <div
                key="admin"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <AdminDashboard user={user} setView={setView} />
              </div>
            )}
            {view === 'SETTINGS' && user?.role === 'ADM' && (
              <motion.div
                key='settings'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <UserSettings />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {user && (
          <nav className='fixed bottom-0 left-0 w-full h-20 z-50 bg-white/90 backdrop-blur-xl border-t border-slate-200/20 flex justify-around items-center px-4 pb-safe md:hidden'>
            <motion.button onClick={() => setView('REGISTRATION')} className={cn('flex flex-col items-center gap-1', view === 'REGISTRATION' ? 'text-[#C81E1E]' : 'text-slate-400')}>
              <Plus className='w-6 h-6' />
              <span className='text-[10px] font-bold uppercase'>Registro</span>
            </motion.button>
            <motion.button onClick={() => setView('ADMIN')} className={cn('flex flex-col items-center gap-1', view === 'ADMIN' ? 'text-[#C81E1E]' : 'text-slate-400')}>
              <Users className='w-6 h-6' />
              <span className='text-[10px] font-bold uppercase'>Painel</span>
            </motion.button>
            {user?.role === 'ADM' && (
              <motion.button onClick={() => setView('SETTINGS')} className={cn('flex flex-col items-center gap-1', view === 'SETTINGS' ? 'text-[#C81E1E]' : 'text-slate-400')}>
                <Settings className='w-6 h-6' />
                <span className='text-[10px] font-bold uppercase'>Ajustes</span>
              </motion.button>
            )}
          </nav>
        )}
      </div>
    </>
  );
}
